import React, { useEffect, useState, useRef, useCallback } from "react";
import DashboardHeader from "../../components/auth/DashboardHeader";
import Sidebar from "../../components/auth/Sidebar";
import { Loader } from "../../components/Loader";
import PaymentLinkTable from "../../components/TransactionsDataTable";
import API from "../../helper/AxiosInterceptor";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCrypto, setSelectedCrypto] = useState("ALL"); // State for selected cryptocurrency
  const [isLoading, setIsLoading] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchTimerRef = useRef(null);

  // Debounce search input
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 300);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      await API.get(
        `${process.env.REACT_APP_API_URL}/payment-link/merchant-tx-list`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            pageNo: currentPage,
            limitVal: 20,
            symbol: selectedCrypto,
            search: debouncedSearch || undefined,
          },
        }
      )
        .then((resp) => {
          console.log("res", resp);
          setTransactions(resp?.data?.data);
          setCurrentPage(resp?.data?.currentPage);
          setTotalPages(resp?.data?.totalPages);
        })
        .catch((error) => {
          console.error("Error in transactions list", error);
        });
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, selectedCrypto, debouncedSearch]);

  const handlePageChange = (pageNo) => {
    if (pageNo > 0 && pageNo <= totalPages) {
      setCurrentPage(pageNo);
    }
  };

  const handleCryptoChange = (e) => {
    setSelectedCrypto(e.target.value);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Prepare transaction data for export
  const getExportData = () => {
    return transactions.map((tx) => ({
      "Transaction ID": tx._id || tx.id || "N/A",
      "Status": tx.status || "N/A",
      "Withdraw Status": tx.withdrawStatus || "N/A",
      "Type": tx.type || "N/A",
      "Buyer Email": tx.buyerEmail || "N/A",
      "Amount": tx.amount || "0",
      "Symbol": tx.symbol || tx.code || "N/A",
      "Chain": tx.chainId || "N/A",
      "Transaction Type": tx.transactionType || "N/A",
      "To Address": tx.toAddress || "N/A",
      "Token Address": tx.tokenAddress || "N/A",
      "Token Decimals": tx.tokenDecimals || "N/A",
      "Link URL": tx.linkURL || "N/A",
      "Created At": formatDate(tx.createdAt),
      "Updated At": formatDate(tx.updatedAt),
    }));
  };

  // Download as PDF
  const downloadPDF = () => {
    const doc = new jsPDF("landscape");
    const exportData = getExportData();

    // Title
    doc.setFontSize(18);
    doc.setTextColor(40);
    doc.text("Transaction Report", 14, 22);

    // Date
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);

    // Table
    const tableColumn = [
      "Transaction ID",
      "Status",
      "Withdraw Status",
      "Type",
      "Buyer Email",
      "Amount",
      "Symbol",
      "Chain",
      "Created At",
    ];

    const tableRows = exportData.map((tx) => [
      tx["Transaction ID"].substring(0, 12) + "...",
      tx["Status"],
      tx["Withdraw Status"],
      tx["Type"],
      tx["Buyer Email"],
      tx["Amount"],
      tx["Symbol"],
      tx["Chain"],
      tx["Created At"],
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [245, 166, 35], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save("transactions_report.pdf");
    setShowDownloadDropdown(false);
  };

  // Download as CSV
  const downloadCSV = () => {
    const exportData = getExportData();
    const headers = Object.keys(exportData[0] || {});
    const csvContent = [
      headers.join(","),
      ...exportData.map((row) =>
        headers.map((header) => `"${row[header] || ""}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "transactions_report.csv";
    link.click();
    setShowDownloadDropdown(false);
  };

  // Download as Excel
  const downloadExcel = () => {
    const exportData = getExportData();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

    // Set column widths
    const colWidths = [
      { wch: 26 }, // Transaction ID
      { wch: 12 }, // Status
      { wch: 15 }, // Withdraw Status
      { wch: 15 }, // Type
      { wch: 25 }, // Buyer Email
      { wch: 12 }, // Amount
      { wch: 10 }, // Symbol
      { wch: 10 }, // Chain
      { wch: 15 }, // Transaction Type
      { wch: 45 }, // To Address
      { wch: 45 }, // Token Address
      { wch: 15 }, // Token Decimals
      { wch: 50 }, // Link URL
      { wch: 20 }, // Created At
      { wch: 20 }, // Updated At
    ];
    worksheet["!cols"] = colWidths;

    XLSX.writeFile(workbook, "transactions_report.xlsx");
    setShowDownloadDropdown(false);
  };

  return (
    <>
      <DashboardHeader />

      <div className="site-bg">
        <div className="dashborad-content-bg">
          <aside className="sidebar">
            <Sidebar />
          </aside>
          <div className="dashborad-warper" style={{ position: "relative" }}>
            {isLoading && <Loader inline={true} />}
            <div className="transactions-list">
              <div className="dashborad-head">
                <div className="breadcrumb-menu">
                  <p>All the transaction of payment links</p>
                </div>
                <div className="dashborad-head-right" style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                  {/* Download Report Dropdown */}
                  <div ref={dropdownRef} style={{ position: "relative" }}>
                    <button
                      type="button"
                      onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                      disabled={!transactions || transactions.length === 0}
                      style={{
                        backgroundColor: "#f5a623",
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        padding: "10px 20px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: transactions && transactions.length > 0 ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        opacity: transactions && transactions.length > 0 ? 1 : 0.6,
                      }}
                    >
                      <i className="ti ti-download" style={{ fontSize: "16px" }}></i>
                      Download Report
                      <i className="ti ti-chevron-down" style={{ fontSize: "14px" }}></i>
                    </button>

                    {showDownloadDropdown && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          marginTop: "5px",
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          minWidth: "180px",
                          zIndex: 100,
                          overflow: "hidden",
                        }}
                      >
                        <button
                          type="button"
                          onClick={downloadPDF}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "none",
                            backgroundColor: "transparent",
                            textAlign: "left",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#333",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                        >
                          <i className="ti ti-file-type-pdf" style={{ color: "#dc3545", fontSize: "18px" }}></i>
                          Download PDF
                        </button>
                        <button
                          type="button"
                          onClick={downloadExcel}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "none",
                            backgroundColor: "transparent",
                            textAlign: "left",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#333",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                        >
                          <i className="ti ti-file-spreadsheet" style={{ color: "#198754", fontSize: "18px" }}></i>
                          Download Excel
                        </button>
                        <button
                          type="button"
                          onClick={downloadCSV}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "none",
                            backgroundColor: "transparent",
                            textAlign: "left",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#333",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                          onMouseEnter={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
                          onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                        >
                          <i className="ti ti-file-text" style={{ color: "#0d6efd", fontSize: "18px" }}></i>
                          Download CSV
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="year-select-box">
                    <select
                      className="form-control"
                      value={selectedCrypto}
                      onChange={handleCryptoChange}
                    >
                      <option value="ALL">All</option>
                      <option value="USDT">USDT</option>
                      <option value="BNB">BNB</option>
                      <option value="ETH">ETH</option>
                      <option value="AVAX">AVAX</option>
                      <option value="USDC">USDC</option>
                      <option value="MATIC">MATIC</option>
                      <option value="TRX">TRX</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Search Box */}
              <div style={{ padding: "15px 20px 0", backgroundColor: "#fff", borderRadius: "8px 8px 0 0", marginBottom: "-1px" }}>
                <div style={{ position: "relative", maxWidth: "450px" }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by Transaction Hash, From, To, or Received amount..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{
                      padding: "10px 15px 10px 40px",
                      borderRadius: "8px",
                      border: "1px solid #ddd",
                      fontSize: "14px",
                      width: "100%",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => e.target.style.borderColor = "#f0b90b"}
                    onBlur={(e) => e.target.style.borderColor = "#ddd"}
                  />
                  <span style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#999",
                    fontSize: "16px",
                    pointerEvents: "none",
                  }}>
                    🔍
                  </span>
                </div>
              </div>

              <div className="white-bg my-apps-content padd20">
                <PaymentLinkTable
                  transactions={transactions}
                  isReceivedAmount={true}
                  isTaxAmount={true}
                  isAmountAfterTax={true}
                />
                <div className="pagination-box">
                  <ul>
                    <li>
                      <a
                        href=""
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage - 1);
                        }}
                        disabled={currentPage === 1}
                      >
                        <img src="images/left-arrow.png" alt="arrow" />
                      </a>
                    </li>
                    {[...Array(totalPages).keys()].map((pageNo) => (
                      <li
                        key={pageNo + 1}
                        className={currentPage === pageNo + 1 ? "active" : ""}
                      >
                        <a
                          href=""
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(pageNo + 1);
                          }}
                        >
                          {pageNo + 1}
                        </a>
                      </li>
                    ))}
                    <li>
                      <a
                        href=""
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage + 1);
                        }}
                        disabled={currentPage === totalPages}
                      >
                        <img src="images/right-arrow.png" alt="arrow" />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Transactions;
